class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    // HC: the cache is shared by every <predictive-search> on the page (Crave's
    // design, kept): the response markup is the same section output whichever
    // instance asked, so a hit fetched by one instance serves the others.
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    // HC: lead-owned "Popular" links, shown only while the term is empty.
    this.popularElement = this.querySelector('[data-search-popular]');
    this.isOpen = false;
    this.abortController = new window.AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.togglePopular(!this.getQuery().length);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    // HC: typing invalidates any arrow-key selection at once, before the
    // debounced search runs, so Enter cannot open a row for the old term.
    this.input.addEventListener('input', this.clearSelection.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  // HC: normalize all whitespace, not just the first space.
  getQueryKey(searchTerm) {
    return searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  clearSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => element.setAttribute('aria-selected', false));
    this.input.removeAttribute('aria-activedescendant');
  }

  togglePopular(show) {
    if (this.popularElement) this.popularElement.hidden = !show;
  }

  // HC: abort any in-flight request so its response can never render.
  abortPendingRequest() {
    this.abortController.abort();
    this.abortController = new window.AbortController();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.togglePopular(!this.searchTerm.length);

    if (!this.searchTerm.length) {
      this.abortPendingRequest();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortPendingRequest();
      this.closeResults(true);
      this.togglePopular(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // HC: Enter on a selected option is handled on keyup; stop the implicit
    // form submit so the footer option does not submit twice.
    if (event.code === 'Enter' && this.querySelector('[aria-selected="true"]')) event.preventDefault();
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // HC: count occurrences with split, not an unescaped RegExp, so terms like
      // "(" or "[" cannot throw.
      if (currentButtonText.split(previousTerm).length - 1 > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      // HC: a function replacement keeps "$&"-style sequences in the term literal.
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  // HC: the term is the last occurrence in the footer copy, so splice there;
  // the copy itself may contain the term (for example "the").
  replaceFooterTerm(cachedTerm, newTerm) {
    const element = this.querySelector('[data-predictive-search-search-for-text]');
    const text = element?.textContent;
    const index = text ? text.lastIndexOf(cachedTerm) : -1;
    if (index < 0) return;
    element.textContent = text.slice(0, index) + newTerm + text.slice(index + cachedTerm.length);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    // HC: the loading and error states have no options to move to.
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getQueryKey(searchTerm);
    // HC: one AbortController per request; the previous request is aborted.
    this.abortPendingRequest();
    const { signal } = this.abortController;
    this.setLiveRegionLoadingState();

    const cached = this.cachedResults[queryKey];
    if (cached) {
      this.renderSearchResults(cached.markup);
      // HC: cache hits ignore case and spacing; show the term as typed now.
      if (cached.term !== searchTerm) this.replaceFooterTerm(cached.term, searchTerm);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    this.renderStatus('info', this.dataset.searchingTitle, this.dataset.searchingBody);
    this.open();
    // HC: a status is not a listbox, so the combobox is not expanded.
    this.input.setAttribute('aria-expanded', false);

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const isCurrent = () => !signal.aborted && searchTerm === this.getQuery();

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced (queried now,
        // so instances added after this one was constructed are included)
        document.querySelectorAll('predictive-search').forEach((predictiveSearchInstance) => {
          if (predictiveSearchInstance.cachedResults) predictiveSearchInstance.cachedResults[queryKey] = { markup: resultsMarkup, term: searchTerm };
        });

        if (!isCurrent()) {
          // HC: stale response for an older term; never render it.
          searchDeferred?.reject(new Error('stale'));
          return;
        }

        this.renderSearchResults(resultsMarkup);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        searchDeferred?.reject(error);
        // Aborts (code 20 / AbortError) are silent; so are failures for a term
        // the shopper has already moved past.
        if (error?.name === 'AbortError' || error?.code === 20 || !isCurrent()) return;

        // HC: errors render inside the panel and keep it open; nothing is
        // cached and nothing is rethrown.
        this.removeAttribute('results');
        this.renderStatus('error', this.dataset.errorTitle, this.dataset.errorBody);
        this.removeAttribute('loading');
        this.setLiveRegionText(this.dataset.errorTitle || '');
        this.open();
        this.input.setAttribute('aria-expanded', false);
      });
  }

  // HC: builds div.hc-status[data-tone] with DOM APIs only (no string HTML).
  renderStatus(tone, title, body) {
    const status = document.createElement('div');
    status.className = 'hc-status';
    status.dataset.tone = tone;
    status.setAttribute('role', tone === 'error' ? 'alert' : 'status');

    const icon = document.createElement('span');
    icon.className = 'hc-status__icon';
    icon.append(this.createStatusIcon(tone));

    const content = document.createElement('span');
    content.className = 'hc-status__body';
    const label = document.createElement('strong');
    label.className = 'hc-status__label';
    label.textContent = title || '';
    content.append(label);
    if (body) {
      const detail = document.createElement('span');
      detail.className = 'hc-status__detail';
      detail.textContent = body;
      content.append(detail);
    }

    status.append(icon, content);
    this.input.removeAttribute('aria-activedescendant');
    this.predictiveSearchResults.replaceChildren(status);
  }

  // HC: the sf-icons "info" and "warning-circle" glyphs, drawn with createElementNS.
  createStatusIcon(tone) {
    const ns = 'http://www.w3.org/2000/svg';
    const make = (name, attributes) => {
      const node = document.createElementNS(ns, name);
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
      return node;
    };
    const svg = make('svg', {
      class: 'hc-icon sf-icon',
      viewBox: '0 0 256 256',
      fill: 'currentColor',
      'aria-hidden': 'true',
      focusable: 'false',
    });
    const stroke = make('g', {
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '16',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
    stroke.append(make('circle', { cx: '128', cy: '128', r: '96' }));
    if (tone === 'error') {
      stroke.append(make('path', { d: 'M128 80v48' }));
      svg.append(stroke, make('circle', { cx: '128', cy: '168', r: '12' }));
    } else {
      stroke.append(make('path', { d: 'M120 120h8v56h8' }));
      svg.append(stroke, make('circle', { cx: '128', cy: '84', r: '12' }));
    }
    return svg;
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.input.removeAttribute('aria-activedescendant');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.togglePopular(true);
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
